import { useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BrandMark, Button, ThemeToggle } from '@clari/ui'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { SessionExpiredModal } from '@/components/SessionExpiredModal'
import { authApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/authStore'
import { useSessionStore } from '@/stores/sessionStore'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const

/** Authenticated shell: header, sidebar nav, idle tracking (15 min), sign-out. */
export function AppLayout() {
  const { t } = useTranslation(['auth', 'banking'])
  const navigate = useNavigate()
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const clear = useAuthStore((s) => s.clear)
  const markActivity = useSessionStore((s) => s.markActivity)
  const expire = useSessionStore((s) => s.expire)

  useEffect(() => {
    const onActivity = () => markActivity()
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    const interval = window.setInterval(() => {
      if (useSessionStore.getState().isIdle()) expire()
    }, 30_000)
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity))
      window.clearInterval(interval)
    }
  }, [markActivity, expire])

  async function handleSignOut() {
    try {
      await authApi.logout(refreshToken ?? undefined)
    } catch {
      /* even if the call fails, drop the local session */
    }
    clear()
    navigate('/login', { replace: true })
  }

  const navItems = [
    { to: '/app', label: t('nav.dashboard', { ns: 'auth' }), end: true },
    { to: '/app/account', label: t('nav.accounts', { ns: 'banking' }), end: false },
    { to: '/app/transactions', label: t('nav.transactions', { ns: 'banking' }), end: false },
    { to: '/app/cards', label: t('nav.cards', { ns: 'banking' }), end: false },
    { to: '/app/fx', label: t('fx.title', { ns: 'banking' }), end: false },
    { to: '/app/account/health', label: t('nav.account_health', { ns: 'auth' }), end: false },
    { to: '/app/security', label: t('nav.security', { ns: 'auth' }), end: false },
  ]

  return (
    <div className="flex min-h-screen flex-col bg-bg text-text">
      <header className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
        <BrandMark size={20} />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle defaultMode="dark" />
          <Button variant="secondary" size="sm" onClick={handleSignOut}>
            {t('nav.sign_out')}
          </Button>
        </div>
      </header>
      <div className="flex flex-1">
        <nav className="hidden w-56 shrink-0 border-r border-border p-4 sm:block">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-bg-subtle text-text' : 'text-text-muted hover:text-text',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="flex-1 p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
      <SessionExpiredModal />
    </div>
  )
}
